/**
 * AdminCheckRecycledTickerButton — Round P120 (Feb 2026).
 *
 * Detects "recycled" tickers — symbols reused by an exchange after
 * the original company was delisted / liquidated. The classic case is
 * LRE: originally a REIT that liquidated in 2015, then the same
 * 3-letter symbol was reissued to Lead Real Estate Co. (ADR) on
 * 2023-09-27. Massive fuses both histories under the current ticker,
 * so the ADR appears to have "dividends from 2015" that actually
 * belong to the dead REIT — polluting charts + firing false
 * "suspended dividend" warnings.
 *
 * Detection rule: if the LATEST dividend ex_date returned by Massive
 * is BEFORE the current entity's `list_date`, the ticker is recycled
 * and Massive has no usable dividend data for the current company.
 *
 * FLOW (per user spec):
 *   1. Admin clicks the button → modal opens showing every candidate
 *      already detected in prior scans (persisted in the
 *      `recycled_ticker_candidates` collection).
 *   2. Each row: clickable TICKER (opens StockModal — this modal
 *      STAYS OPEN so the admin can flip between tickers + Google
 *      verify — closing StockModal does NOT close the recycled list)
 *      + trash icon (permanently blocklists that single ticker via
 *      the existing /api/admin/blocklist/{ticker} endpoint).
 *   3. NOTHING happens automatically. Nothing bulk-processes the
 *      list. The only action that mutates state is clicking the
 *      trash icon on one row at a time.
 *   4. Optional "Rescan now" button re-runs the backend detector
 *      across all stock_cache tickers with list_date >= 2015.
 *
 * Security (defense in depth, mirrors AdminCheckLiquidationButton):
 *   - Layer 1: Component self-gates on `user.role !== 'admin'`.
 *   - Layer 2: Every backend endpoint gates on `role != 'admin'`.
 *   - Layer 3: Caller in Dashboard.js only renders inside the admin
 *              button group which is itself wrapped in `{user.role==='admin'}`.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, X, Trash2, ExternalLink, CheckCircle2,
  Recycle, AlertTriangle, Search, TrendingUp, CircleDollarSign,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { RecycledChevron, useRecycledLockedTickers } from '@/hooks/useRecycledLockedTickers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCheckRecycledTickerButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);
  // Two-stage trash confirmation — first click arms the confirm on
  // that specific ticker, second click within the same row commits.
  const [armedTicker, setArmedTicker] = useState(null);
  const [armedKeepTicker, setArmedKeepTicker] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  // Round P120b — single-ticker "Check This One" input for immediate
  // verification without waiting for the 17-minute background scan.
  const [singleTicker, setSingleTicker] = useState('');
  const [singleBusy, setSingleBusy] = useState(false);
  const [singleResult, setSingleResult] = useState(null);
  // Polling ref for background scan progress.
  const pollRef = useRef(null);

  const fetchList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/recycled-tickers`, {
        withCredentials: true,
      });
      setReport((prev) => {
        // Detect scan-completion transition (running → complete/error)
        // so we can stop polling + show a toast.
        const prevStatus = prev?.scan_status?.status;
        const newStatus = data?.scan_status?.status;
        if (prevStatus === 'running' && newStatus && newStatus !== 'running') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          setScanning(false);
          if (newStatus === 'complete') {
            toast.success('Recycled-ticker scan complete', {
              description: `Scanned ${data.scan_status.scanned || 0} · found ${data.scan_status.found || 0} recycled · cleared ${data.scan_status.cleared || 0} repaired.`,
              duration: 8000,
            });
          } else if (newStatus === 'error') {
            toast.error('Scan errored out', {
              description: data.scan_status.error || 'See backend logs.',
            });
          }
        }
        return data;
      });
      const st = data?.scan_status?.status;
      if (st === 'running') setScanning(true);
    } catch (err) {
      toast.error('Could not load recycled tickers', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Stop polling on unmount / modal close.
  useEffect(() => {
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  if (user?.role !== 'admin') return null;

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    // Poll every 5 s so the progress bar moves without hammering the API.
    pollRef.current = setInterval(async () => {
      await fetchList(true);
    }, 5000);
  };

  const openModal = async () => {
    setOpen(true);
    setArmedTicker(null);
    setSingleResult(null);
    await fetchList();
    // If a scan is already running (from a prior session), start polling.
    const status = (await axios.get(`${API}/admin/recycled-tickers`, { withCredentials: true }))
      .data?.scan_status?.status;
    if (status === 'running') {
      setScanning(true);
      startPolling();
    }
  };

  const runScan = async () => {
    if (scanning) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/recycled-tickers/scan`,
        {},
        { withCredentials: true },
      );
      if (data.already_running) {
        toast.info('Scan already in progress', {
          description: `Started ${data.started_at ? new Date(data.started_at).toLocaleTimeString() : 'recently'} — polling for progress.`,
        });
        setScanning(true);
        startPolling();
        return;
      }
      toast.success('Recycled-ticker scan started', {
        description: 'Running in the background. Progress appears in the modal — you can close it and come back.',
        duration: 6000,
      });
      setScanning(true);
      await fetchList(true);
      startPolling();
    } catch (err) {
      toast.error('Could not start scan', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const checkSingle = async () => {
    const t = (singleTicker || '').trim().toUpperCase();
    if (!t || singleBusy) return;
    setSingleBusy(true);
    setSingleResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/recycled-tickers/check/${encodeURIComponent(t)}`,
        {},
        { withCredentials: true },
      );
      setSingleResult(data);
      if (data.recycled) {
        toast.success(`${t} confirmed recycled`, {
          description: `list_date ${String(data.list_date || '').slice(0, 10)} · latest ex ${String(data.latest_ex_date || '').slice(0, 10)}. Added to the list below.`,
        });
        await fetchList(true);
      } else if (data.list_date && data.latest_ex_date) {
        toast.info(`${t} not recycled`, {
          description: `latest ex_date ${String(data.latest_ex_date).slice(0, 10)} is AFTER list_date ${String(data.list_date).slice(0, 10)} — Massive data is current.`,
        });
      } else {
        toast.warning(`${t} — inconclusive`, {
          description: 'Missing list_date or dividend history from Massive. Nothing added.',
        });
      }
    } catch (err) {
      toast.error(`Check failed for ${t}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSingleBusy(false);
    }
  };

  // Round P120c — Some recycled tickers are legit companies now, they
  // just don't pay dividends (e.g. the reused symbol went to a non-div
  // growth stock). Instead of blocklisting outright, admin sends the
  // ticker to the Pending Non-Dividend Review queue — mirrors the
  // arrow flow in the Div → Non-Div reconciliation modal. Uses the
  // same backend endpoint (POST /admin/mark-non-dividend/{ticker}).
  const moveToNonDivReview = async (ticker) => {
    if (busyTicker) return;
    setBusyTicker(ticker);
    try {
      await axios.post(
        `${API}/admin/mark-non-dividend/${encodeURIComponent(ticker)}`,
        { reason: 'recycled_ticker_now_non_dividend' },
        { withCredentials: true },
      );
      toast.success(`${ticker} → Pending Non-Div Review`, {
        description: 'Queued. Open the Pending Non-Dividend Review panel to Accept + hydrate.',
        duration: 4500,
      });
      // Optimistically drop the row from the visible list.
      setReport((prev) => prev
        ? {
          ...prev,
          candidates: (prev.candidates || []).filter((c) => c.ticker !== ticker),
          count: Math.max(0, (prev.count || 1) - 1),
        }
        : prev);
      // Also drop from the persisted recycled candidate list so it
      // doesn't reappear on the next scan.
      try {
        await axios.delete(
          `${API}/admin/recycled-tickers/${encodeURIComponent(ticker)}`,
          { withCredentials: true },
        );
      } catch (_e) { /* non-fatal — the row is already visually gone */ }
    } catch (err) {
      toast.error(`Could not move ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Aug 2026 (ARKY) — Third action: the recycled ticker's NEW owner IS
  // (or will be) a dividend payer (e.g. ARKY = ARK's monthly-income
  // ETF, reusing a dead 2024 company's symbol). Purges the fused old
  // history AND sets a permanent dividend floor (= list_date) so the
  // Massive fetch can never re-poison the row — while KEEPING the
  // ticker eligible for dividend status. 2-click confirm like trash.
  const keepAsDivPayer = async (ticker) => {
    if (busyTicker) return;
    if (armedKeepTicker !== ticker) {
      setArmedKeepTicker(ticker);
      setTimeout(() => {
        setArmedKeepTicker((cur) => (cur === ticker ? null : cur));
      }, 5000);
      return;
    }
    setBusyTicker(ticker);
    try {
      const { data } = await axios.post(
        `${API}/admin/recycled-tickers/${encodeURIComponent(ticker)}/keep-as-div-payer`,
        {},
        { withCredentials: true },
      );
      toast.success(`${ticker} kept as dividend payer`, {
        description: `Fused pre-${data?.floor_date} history purged + floor set. New payouts will build a clean history automatically.`,
        duration: 6000,
      });
      setReport((prev) => prev
        ? {
          ...prev,
          candidates: (prev.candidates || []).filter((c) => c.ticker !== ticker),
          count: Math.max(0, (prev.count || 1) - 1),
        }
        : prev);
      setArmedKeepTicker(null);
    } catch (err) {
      toast.error(`Could not remediate ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Trash icon — blocklists PERMANENTLY. 2-stage: first click arms
  // the row (icon turns red + shakes), second click commits. No
  // other action available; per user spec "nothing else happens to
  // the tickers on the list, only if i hit the trash can icon".
  const trashTicker = async (ticker) => {
    if (busyTicker) return;
    if (armedTicker !== ticker) {
      setArmedTicker(ticker);
      // Auto-disarm after 5 seconds so a mis-click doesn't stay armed.
      setTimeout(() => {
        setArmedTicker((cur) => (cur === ticker ? null : cur));
      }, 5000);
      return;
    }
    setBusyTicker(ticker);
    try {
      const { data } = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(ticker)}`,
        { reason: 'recycled_ticker_fused_data' },
        { withCredentials: true },
      );
      const removed = data?.removed || {};
      const summary = Object.entries(removed)
        .filter(([, n]) => Number(n) > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(', ') || 'no cached rows';
      toast.success(`${ticker} banned to the bad-ticker database`, {
        description: `Purged (${summary}). Blocklist size now ${data?.blocklist_size}.`,
      });
      // Optimistically drop the row from the visible list.
      setReport((prev) => prev
        ? {
          ...prev,
          candidates: (prev.candidates || []).filter((c) => c.ticker !== ticker),
          count: Math.max(0, (prev.count || 1) - 1),
        }
        : prev);
      setArmedTicker(null);
    } catch (err) {
      toast.error(`Could not blocklist ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Ticker click → opens the StockModal via the parent's handler.
  // Critical: we do NOT close this modal. The admin needs to flip
  // between tickers + Google-verify before trashing.
  const inspectTicker = (ticker, e) => {
    e?.stopPropagation?.();
    if (typeof onStockClick === 'function') {
      onStockClick(ticker, null, { previewOnly: true });
    }
  };

  const closeModal = () => {
    setOpen(false);
    setArmedTicker(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
        data-testid="admin-check-recycled-ticker-btn"
        title="Detect tickers where Massive fused dividend history from a previous, delisted company using the same symbol (e.g. LRE). Click ticker to inspect + verify. Trash icon permanently blocklists."
      >
        <Recycle className="w-3 h-3" />
        Check Recycled Ticker
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={closeModal}
          data-testid="admin-recycled-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <Recycle className="w-4 h-4 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">Recycled Tickers</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  Latest Massive dividend predates the current entity&apos;s list_date — fused history from a prior delisted company.
                </div>
              </div>
              <button
                type="button"
                onClick={runScan}
                disabled={scanning || loading}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-recycled-rescan-btn"
                title="Re-run the detector across all stock_cache tickers with list_date >= 2015. Takes 15–20 minutes."
              >
                {scanning
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <RefreshCw className="w-3 h-3" />}
                {scanning ? 'Scanning…' : 'Rescan now'}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-recycled-close"
                aria-label="Close recycled tickers modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {/* Round P120b — Single-ticker "Check This One" input.
                  Runs the detector on ONE ticker (2 Massive calls, ~1 sec)
                  and adds it to the list if recycled — no waiting on the
                  17-min bulk scan. Perfect for immediately vetting LRE. */}
              <div
                className="border border-violet-400/40 rounded-md p-3 bg-violet-500/5"
                data-testid="recycled-single-check-panel"
              >
                <div className="text-[11px] font-bold uppercase tracking-wide text-violet-800 dark:text-violet-200 mb-1.5 flex items-center gap-1.5">
                  <Search className="w-3 h-3" /> Check a single ticker
                </div>
                <div className="text-[10.5px] text-muted-foreground mb-2">
                  Fast (~1 sec). Detects recycled tickers on the spot without waiting for the 17-min bulk scan.
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={singleTicker}
                    onChange={(e) => setSingleTicker(e.target.value.toUpperCase().slice(0, 15))}
                    onKeyDown={(e) => { if (e.key === 'Enter') checkSingle(); }}
                    placeholder="e.g. LRE"
                    className="flex-1 px-2 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-violet-500 font-mono uppercase"
                    data-testid="recycled-single-input"
                    disabled={singleBusy}
                  />
                  <button
                    type="button"
                    onClick={checkSingle}
                    disabled={singleBusy || !singleTicker.trim()}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="recycled-single-check-btn"
                  >
                    {singleBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                    Check
                  </button>
                </div>
                {singleResult && !singleBusy && (
                  <div
                    className={`mt-2 px-2 py-1.5 rounded text-[10.5px] ${
                      singleResult.recycled && !singleResult.auto_fixed
                        ? 'bg-rose-500/15 text-rose-800 dark:text-rose-200 border border-rose-500/40'
                        : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 border border-emerald-500/30'
                    }`}
                    data-testid="recycled-single-result"
                  >
                    <b>{singleResult.ticker}</b>: {singleResult.recycled ? 'RECYCLED' : 'not recycled'}{' '}
                    {singleResult.list_date && `· list_date ${String(singleResult.list_date).slice(0, 10)}`}{' '}
                    {singleResult.latest_ex_date && `· latest ex ${String(singleResult.latest_ex_date).slice(0, 10)}`}
                    {singleResult.recycled && (
                      singleResult.auto_fixed
                        ? <> · <b>AUTO-FIXED</b> — fused old history purged, dividend floor set at {String(singleResult.floor_date || singleResult.list_date).slice(0, 10)}. Stays in the dividend universe, new payouts build a clean history.</>
                        : <> · added to list below</>
                    )}
                  </div>
                )}
              </div>

              {/* Scan progress banner — visible while the background
                  scan is running. Poller refreshes every 5s. */}
              {report?.scan_status?.status === 'running' && (
                <div
                  className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-violet-500/15 border border-violet-500/40 text-violet-900 dark:text-violet-200 text-[11px]"
                  data-testid="recycled-scan-progress"
                >
                  <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin" />
                  <div className="flex-1">
                    <div className="font-bold">Background scan running…</div>
                    <div>
                      Scanned <b>{report.scan_status.scanned || 0}</b> of{' '}
                      <b>{report.scan_status.total || 0}</b> · found{' '}
                      <b>{report.scan_status.found || 0}</b> recycled so far.
                    </div>
                    {report.scan_status.total > 0 && (
                      <div className="w-full h-1 bg-violet-500/20 rounded overflow-hidden mt-1.5">
                        <div
                          className="h-full bg-violet-600 transition-all duration-500"
                          style={{
                            width: `${Math.min(100, Math.round(100 * (report.scan_status.scanned || 0) / report.scan_status.total))}%`,
                          }}
                        />
                      </div>
                    )}
                    <div className="text-[10px] italic mt-1 text-violet-700/80 dark:text-violet-300/70">
                      Safe to close the modal — the scan continues in the background. Reopen anytime to check progress.
                    </div>
                  </div>
                </div>
              )}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              )}

              {!loading && report && (
                <>
                  <div className="text-[11px] text-muted-foreground flex items-center justify-between" data-testid="recycled-count">
                    <span>{report.count} candidate{report.count === 1 ? '' : 's'}.</span>
                    {report.last_scan_at && (
                      <span className="italic">
                        last scan: {String(report.last_scan_at).slice(0, 10)}
                      </span>
                    )}
                  </div>

                  {report.count === 0 && (
                    <div
                      className="flex items-start gap-2 px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm"
                      data-testid="recycled-empty"
                    >
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                      <div>
                        No recycled tickers detected. Click <b>Rescan now</b> to re-run the detector across every ticker with list_date ≥ 2015.
                      </div>
                    </div>
                  )}

                  {report.count > 0 && (
                    <>
                      <div
                        className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-[11px]"
                        data-testid="recycled-warning-banner"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <div>
                          Click a <b>ticker</b> to inspect it in the Stock Modal (this list stays open — verify with external sources first). Click the <b>arrow</b> to send a ticker to <b>Pending Non-Div Review</b> (recycled but the new company is legit — just doesn&apos;t pay dividends). Click the <b>gold coin</b> if the new owner <b>IS a dividend payer</b> (like ARKY) — purges the old fused history and locks a floor so it stays clean while new payouts build naturally. Click the <b>trash icon</b> to permanently blocklist that single ticker. Two clicks on coin/trash = confirm.
                        </div>
                      </div>

                      <div className="border border-border/60 rounded-md overflow-hidden">
                        <div className="grid grid-cols-[28px_28px_70px_1fr_100px_100px_60px_36px] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-bold uppercase tracking-wide text-muted-foreground items-center">
                          <div />
                          <div />
                          <div>Ticker</div>
                          <div>Name</div>
                          <div className="text-right">List date</div>
                          <div className="text-right">Latest ex_date</div>
                          <div className="text-right" title="Number of dividends that predate list_date">Old #</div>
                          <div />
                        </div>
                        <div className="divide-y divide-border/40">
                          {report.candidates.map((c) => {
                            const armed = armedTicker === c.ticker;
                            const armedKeep = armedKeepTicker === c.ticker;
                            const busy = busyTicker === c.ticker;
                            return (
                              <div
                                key={c.ticker}
                                className="grid grid-cols-[28px_28px_70px_1fr_100px_100px_60px_36px] gap-2 px-3 py-1.5 items-center text-xs hover:bg-muted/30"
                                data-testid={`recycled-row-${c.ticker}`}
                              >
                                {/* Arrow — FIRST column. Physical separation
                                    from the trash icon on the far right
                                    prevents muscle-memory "click right =
                                    trash" from wiping a legit non-div
                                    candidate. Sends to Pending Non-Div
                                    Review, NOT the permanent blocklist. */}
                                <button
                                  type="button"
                                  onClick={() => moveToNonDivReview(c.ticker)}
                                  disabled={busy}
                                  className="text-muted-foreground hover:text-emerald-600 disabled:opacity-40 shrink-0 inline-flex items-center justify-center"
                                  title={`Move ${c.ticker} to Pending Non-Dividend Review — for recycled tickers that are legit companies but no longer pay dividends.`}
                                  data-testid={`recycled-move-nondiv-${c.ticker}`}
                                >
                                  {busy
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <TrendingUp className="w-3.5 h-3.5" />}
                                </button>
                                {/* Gold coin — SECOND column. The new
                                    owner of the recycled symbol IS a
                                    dividend payer (ARKY case): purge
                                    fused old history + set a permanent
                                    dividend floor, keep the ticker in
                                    the dividend universe. */}
                                <button
                                  type="button"
                                  onClick={() => keepAsDivPayer(c.ticker)}
                                  disabled={busy}
                                  className={`inline-flex items-center justify-center w-6 h-6 rounded transition-all disabled:opacity-40 ${
                                    armedKeep
                                      ? 'bg-amber-500 text-white ring-2 ring-amber-300 animate-pulse'
                                      : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10'
                                  }`}
                                  title={armedKeep
                                    ? `Click again to confirm: purge ${c.ticker}'s fused old history + keep it as a dividend payer`
                                    : `${c.ticker}'s new owner IS a dividend payer — purge the old fused history, set a floor at list_date, and let new payouts build a clean history. Two clicks to confirm.`}
                                  data-testid={`recycled-keep-div-${c.ticker}`}
                                >
                                  {busy
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <CircleDollarSign className="w-3.5 h-3.5" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => inspectTicker(c.ticker, e)}
                                  className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline inline-flex items-center gap-0.5 text-left"
                                  title={`Inspect ${c.ticker} in Stock Modal. This list stays open.`}
                                  data-testid={`recycled-inspect-${c.ticker}`}
                                >
                                  <RecycledChevron ticker={c.ticker} />
                                  {c.ticker} <ExternalLink className="w-2.5 h-2.5" />
                                </button>
                                <div className="truncate text-muted-foreground flex items-center gap-1.5 min-w-0" title={c.name || ''}>
                                  {/* Round P153 — Inactive SPAC tag. Queued by the
                                      SPAC lifecycle watcher when a de-SPAC completes
                                      or the shell liquidates: the old ticker is dead,
                                      trash-blocklist it. */}
                                  {c.reason === 'inactive_spac' && (
                                    <span
                                      className="shrink-0 px-1 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-wide bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border border-red-400/50"
                                      title={`Dead SPAC ticker — ${c.spac_stage === 'liquidated' ? 'the shell liquidated and was delisted' : c.spac_stage === 'ticker_changed' ? `the SPAC renamed its symbol${c.spac_new_ticker ? ` to ${c.spac_new_ticker}` : ''} ahead of its pending merger — the old symbol is dead` : `the de-SPAC merger completed${c.spac_new_ticker ? ` (now trades as ${c.spac_new_ticker})` : ''}`}. Trash-blocklist it.`}
                                      data-testid={`recycled-inactive-spac-${c.ticker}`}
                                    >
                                      Inactive SPAC{c.spac_new_ticker ? ` → ${c.spac_new_ticker}` : ''}
                                    </span>
                                  )}
                                  <span className="truncate">{c.name || '—'}</span>
                                </div>
                                <div className="text-right font-mono text-[10.5px]" title="Current entity IPO / listing date per Massive">
                                  {String(c.list_date || '').slice(0, 10) || '—'}
                                </div>
                                <div className="text-right font-mono text-[10.5px] text-rose-700 dark:text-rose-300" title="Newest dividend in Massive — belongs to the OLD (delisted) company">
                                  {String(c.latest_ex_date || '').slice(0, 10) || '—'}
                                </div>
                                <div className="text-right font-mono text-[10.5px]">
                                  {c.old_dividend_count || 0}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => trashTicker(c.ticker)}
                                  disabled={busy}
                                  className={`inline-flex items-center justify-center w-6 h-6 rounded transition-all disabled:opacity-40 ${
                                    armed
                                      ? 'bg-rose-600 text-white ring-2 ring-rose-400 animate-pulse'
                                      : 'text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10'
                                  }`}
                                  title={armed
                                    ? `Click again to permanently blocklist ${c.ticker}`
                                    : `Blocklist ${c.ticker} (bad-ticker database). Two clicks to confirm.`}
                                  data-testid={`recycled-trash-${c.ticker}`}
                                >
                                  {busy
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <Trash2 className="w-3 h-3" />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-border/60 flex items-center justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-muted"
                data-testid="admin-recycled-close-footer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
